/**
 * MemberNotification — GroupSoup-native design.
 * Collapsed summary cards in a grid. Clicking a card slides open
 * a right-side drawer with the full notification list for that group.
 *
 * Data is sourced from the real notifications API (trpc.notification.getAll).
 * Notifications are grouped by group_id found in the JSONB `data` field.
 * Group names are resolved dynamically via trpc.group.getById.
 */

import { useState, useEffect, useMemo } from 'react';
import styles from '../styles/MemberNotification.module.css';
import { useAuth } from '../contexts/AuthContext';
import { trpc } from '../utils/trpc';

// ─── Types ────────────────────────────────────────────────────────────────────

type NotificationType = 'group_invite' | 'group_post' | 'info' | string;

interface RawNotification {
  id: string;
  created_at: string;
  recipient_id: string;
  sender_id: string;
  type: NotificationType;
  data: {
    message: string;
    group_id?: string;
    [key: string]: unknown;
  };
  is_read: boolean;
  action_status: 'pending' | 'accepted' | 'declined' | 'none';
}

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  time: string;
  read: boolean;
  actionStatus: RawNotification['action_status'];
  raw: RawNotification;
}

interface NotificationGroup {
  groupId: string | null;
  groupName: string;
  unread: number;
  notifications: Notification[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  group_invite: 'Invite',
  group_post: 'Post',
  info: 'Info',
};

function getTypeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

const AVATAR_GRADIENTS = [
  'linear-gradient(145deg,#fcb31b,#e67919)',
  'linear-gradient(145deg,#4a7a47,#80af7d)',
  'linear-gradient(145deg,#2d4f2b,#4a7a47)',
  'linear-gradient(145deg,#527d4f,#80af7d)',
  'linear-gradient(145deg,#e67919,#fcb31b)',
];

function getGradient(char: string): string {
  return AVATAR_GRADIENTS[char.charCodeAt(0) % AVATAR_GRADIENTS.length];
}

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function groupNotifications(
  raw: RawNotification[],
  groupNames: Record<string, string>,
): NotificationGroup[] {
  const map = new Map<string, NotificationGroup>();

  for (const n of raw) {
    const groupId = n.data?.group_id ?? null;
    const key = groupId ?? '__general__';

    if (!map.has(key)) {
      map.set(key, {
        groupId,
        groupName: groupId ? (groupNames[groupId] ?? 'Loading...') : 'General',
        unread: 0,
        notifications: [],
      });
    }

    const group = map.get(key)!;
    const notif: Notification = {
      id: n.id,
      type: n.type,
      message: n.data?.message ?? '',
      time: timeAgo(n.created_at),
      read: n.is_read,
      actionStatus: n.action_status,
      raw: n,
    };

    group.notifications.push(notif);
    if (!n.is_read) group.unread++;
  }

  return Array.from(map.values());
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  group: NotificationGroup;
  onClick: () => void;
  animationDelay: string;
}

function SummaryCard({ group, onClick, animationDelay }: SummaryCardProps) {
  const initial = group.groupName.charAt(0).toUpperCase();
  const latest = group.notifications[0];
  const types = Array.from(new Set(group.notifications.map((n) => n.type)));

  return (
    <button
      className={`${styles.summaryCard} ${group.unread > 0 ? styles.summaryHasUnread : ''}`}
      onClick={onClick}
      style={{ animationDelay }}
      aria-label={`View notifications for ${group.groupName}`}
    >
      {/* Top row */}
      <div className={styles.summaryTop}>
        <div className={styles.summaryAvatarWrap}>
          <div
            className={styles.summaryGroupAvatar}
            style={{ background: getGradient(initial) }}
          >
            {initial}
          </div>
          {group.unread > 0 && (
            <span className={styles.summaryBadge}>{group.unread}</span>
          )}
        </div>
        <div className={styles.summaryMeta}>
          <span className={styles.summaryGroupName}>{group.groupName}</span>
        </div>
        <span className={styles.summaryArrow}>›</span>
      </div>

      {/* Notification count */}
      <div className={styles.summaryActors}>
        <span className={styles.summaryActorCount}>
          {group.notifications.length} notification
          {group.notifications.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Latest preview */}
      {latest && (
        <div className={styles.summaryPreview}>
          <span className={styles.summaryPreviewMsg}>{latest.message}</span>
          <span className={styles.summaryPreviewTime}> · {latest.time}</span>
        </div>
      )}

      {/* Type pills */}
      <div className={styles.summaryPills}>
        {types.map((t) => (
          <span key={t} className={`${styles.typePill} ${styles[t] ?? ''}`}>
            {getTypeLabel(t)}
          </span>
        ))}
      </div>
    </button>
  );
}

// ─── Drawer Notification Item ─────────────────────────────────────────────────

interface NotifItemProps {
  notif: Notification;
  onRead: (id: string) => void;
  onRespond: (id: string, action: 'accepted' | 'declined') => void;
}

function DrawerNotifItem({ notif, onRead, onRespond }: NotifItemProps) {
  const initial = notif.type.charAt(0).toUpperCase();

  return (
    <div
      className={`${styles.drawerNotifItem} ${!notif.read ? styles.unread : ''}`}
      onClick={() => !notif.read && onRead(notif.id)}
    >
      {!notif.read && <div className={styles.unreadDot} />}

      <div className={styles.actorAvatar} style={{ background: getGradient(initial) }}>
        {initial}
      </div>

      <div className={styles.notifBody}>
        <p className={styles.notifText}>
          <span className={styles.notifMessage}>{notif.message}</span>
        </p>
        <span className={styles.notifTime}>{notif.time}</span>

        {/* Accept / Decline for pending invites */}
        {notif.type === 'group_invite' && notif.actionStatus === 'pending' && (
          <div className={styles.actionButtons}>
            <button
              className={styles.acceptBtn}
              onClick={(e) => {
                e.stopPropagation();
                onRespond(notif.id, 'accepted');
              }}
            >
              Accept
            </button>
            <button
              className={styles.declineBtn}
              onClick={(e) => {
                e.stopPropagation();
                onRespond(notif.id, 'declined');
              }}
            >
              Decline
            </button>
          </div>
        )}

        {/* Show resolved status */}
        {notif.type === 'group_invite' &&
          notif.actionStatus !== 'pending' &&
          notif.actionStatus !== 'none' && (
            <span className={styles.resolvedStatus}>
              {notif.actionStatus === 'accepted' ? '✓ Accepted' : '✗ Declined'}
            </span>
          )}
      </div>

      <span className={`${styles.typePill} ${styles[notif.type] ?? ''}`}>
        {getTypeLabel(notif.type)}
      </span>
    </div>
  );
}

// ─── Drawer Panel ─────────────────────────────────────────────────────────────

interface DrawerProps {
  group: NotificationGroup | null;
  onClose: () => void;
  onReadAll: (groupId: string | null) => void;
  onReadOne: (notifId: string) => void;
  onRespond: (notifId: string, action: 'accepted' | 'declined') => void;
}

function DrawerPanel({ group, onClose, onReadAll, onReadOne, onRespond }: DrawerProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div
        className={`${styles.drawerBackdrop} ${group ? styles.backdropVisible : ''}`}
        onClick={onClose}
      />
      <div className={`${styles.drawer} ${group ? styles.drawerOpen : ''}`}>
        {group && (
          <>
            <div className={styles.drawerHeader}>
              <div className={styles.drawerHeaderLeft}>
                <div
                  className={styles.drawerGroupAvatar}
                  style={{
                    background: getGradient(group.groupName.charAt(0).toUpperCase()),
                  }}
                >
                  {group.groupName.charAt(0).toUpperCase()}
                </div>
                <div className={styles.drawerGroupMeta}>
                  <span className={styles.drawerGroupName}>{group.groupName}</span>
                </div>
              </div>
              <div className={styles.drawerHeaderRight}>
                {group.unread > 0 && (
                  <button
                    className={styles.drawerMarkAllBtn}
                    onClick={() => onReadAll(group.groupId)}
                  >
                    Mark all read
                  </button>
                )}
                <button
                  className={styles.drawerCloseBtn}
                  onClick={onClose}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className={styles.drawerList}>
              {group.notifications.map((notif, i) => (
                <div
                  key={notif.id}
                  className={styles.drawerNotifRow}
                  style={{ animationDelay: `${0.04 * i}s` }}
                >
                  <DrawerNotifItem
                    notif={notif}
                    onRead={onReadOne}
                    onRespond={onRespond}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MemberNotification() {
  const { session } = useAuth();

  // ── Queries ──
  const memberProfileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });

  const notificationsQuery = trpc.notification.getAll.useQuery(undefined, {
    enabled: !!session,
    retry: false,
    refetchInterval: 30_000,
  });

  // ── Extract unique group IDs from notifications ──
  const uniqueGroupIds = useMemo(() => {
    const raw = (notificationsQuery.data ?? []) as RawNotification[];
    return Array.from(
      new Set(raw.map((n) => n.data?.group_id).filter((id): id is string => !!id)),
    );
  }, [notificationsQuery.data]);

  // ── Parallel group name lookups via groupRouter.getById ──
  const groupQueries = trpc.useQueries((t) =>
    uniqueGroupIds.map((id) => t.group.getById({ id })),
  );

  // Build a resolved name map once all group queries settle
  const resolvedGroupNames = useMemo(() => {
    const resolved: Record<string, string> = {};
    groupQueries.forEach((q, i) => {
      if (q.data?.name) {
        resolved[uniqueGroupIds[i]] = q.data.name;
      }
    });
    return resolved;
  }, [groupQueries.map((q) => q.data?.name).join(','), uniqueGroupIds]);

  // ── Mutations ──
  const markReadMutation = trpc.notification.markRead.useMutation({
    onSuccess: () => notificationsQuery.refetch(),
  });

  const respondMutation = trpc.notification.respond.useMutation({
    onSuccess: () => notificationsQuery.refetch(),
  });

  // ── Local UI state ──
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);

  // ── Derived data ──

  const groups = useMemo(() => {
    const raw = (notificationsQuery.data ?? []) as RawNotification[];
    return groupNotifications(raw, resolvedGroupNames);
  }, [notificationsQuery.data, resolvedGroupNames]);

  const totalUnread = groups.reduce((sum, g) => sum + g.unread, 0);

  const visibleGroups = filter === 'unread' ? groups.filter((g) => g.unread > 0) : groups;

  const activeGroup = useMemo(
    () => groups.find((g) => (g.groupId ?? '__general__') === activeGroupKey) ?? null,
    [groups, activeGroupKey],
  );

  // ── Handlers ──
  const handleReadOne = (notifId: string) => {
    markReadMutation.mutate({ id: notifId });
  };

  const handleReadAll = (groupId: string | null) => {
    const target = groups.find((g) => g.groupId === groupId);
    if (!target) return;
    target.notifications
      .filter((n) => !n.read)
      .forEach((n) => markReadMutation.mutate({ id: n.id }));
  };

  const handleReadAllGlobal = () => {
    groups
      .flatMap((g) => g.notifications)
      .filter((n) => !n.read)
      .forEach((n) => markReadMutation.mutate({ id: n.id }));
  };

  const handleRespond = (notifId: string, action: 'accepted' | 'declined') => {
    respondMutation.mutate({ notificationId: notifId, action });
  };

  // ── Loading skeleton ──
  if (memberProfileQuery.isLoading || notificationsQuery.isLoading) {
    return (
      <div className={styles.page}>
        <div className={`${styles.memberHeader} ${styles.skeleton}`} />
        <div className={styles.skeletonGrid}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className={styles.skeletonCard}
              style={{ animationDelay: `${0.1 * i}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ──
  if (notificationsQuery.isError) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyState}>
          <p className={styles.emptyText}>
            Failed to load notifications. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.bgRing1} />
      <div className={styles.bgRing2} />

      {/* ── Member Header ── */}
      {/* <div className={styles.memberHeader}>
        <div className={styles.memberLeft}>
          <div
            className={styles.memberAvatar}
            style={{ background: getGradient(displayInitial) }}
          >
            {displayInitial}
          </div>
          <div className={styles.memberInfo}>
            <h1 className={styles.memberName}>{displayName}</h1>
            {displayHandle && (
              <span className={styles.memberHandle}>@{displayHandle}</span>
            )}
          </div>
        </div>
        <div className={styles.memberRight}>
          {totalUnread > 0 && (
            <span className={styles.totalUnread}>{totalUnread} unread</span>
          )}
        </div>
      </div> */}

      {/* ── Page Controls ── */}
      <div className={styles.pageControls}>
        <div className={styles.titleRow}>
          <h2 className={styles.pageTitle}>Notifications</h2>
          {totalUnread > 0 && <span className={styles.titleBadge}>{totalUnread}</span>}
        </div>
        <div className={styles.controlsRight}>
          <div className={styles.filterTabs}>
            {(['all', 'unread'] as const).map((f) => (
              <button
                key={f}
                className={`${styles.filterTab} ${filter === f ? styles.activeTab : ''}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : 'Unread'}
              </button>
            ))}
          </div>
          <button
            className={styles.markAllGlobalBtn}
            onClick={handleReadAllGlobal}
            disabled={totalUnread === 0}
          >
            Mark all read
          </button>
        </div>
      </div>

      {/* ── Summary Card Grid ── */}
      <div className={styles.cardGrid}>
        {visibleGroups.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyText}>You're all caught up!</p>
          </div>
        ) : (
          visibleGroups.map((group, i) => (
            <SummaryCard
              key={group.groupId ?? '__general__'}
              group={group}
              onClick={() => setActiveGroupKey(group.groupId ?? '__general__')}
              animationDelay={`${0.07 * i}s`}
            />
          ))
        )}
      </div>

      {/* ── Drawer ── */}
      <DrawerPanel
        group={activeGroup}
        onClose={() => setActiveGroupKey(null)}
        onReadAll={handleReadAll}
        onReadOne={handleReadOne}
        onRespond={handleRespond}
      />
    </div>
  );
}
